import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import { useTrackingWithTreatment } from '@/composables/useTrackingWithTreatment';
import { getHomeOwnerWorldApi } from '@/helpers/Api';
import { getAxiosClient } from '@/helpers/Axios/axiosClient';
import { getPropertyDetailsPagePath } from '@/helpers/Url/Url';
import { OfferType } from '@/models/valuationFunnel';
import { RESULT_PATH, UBS_LANDING_PAGE_PATH } from '@/router/routes/constants';
import { useCommonStore } from '@/stores/common/common';
import { usePropertyDetailsStore } from '@/stores/propertyDetails/propertyDetails';
import { usePropertyValuationStore } from '@/stores/propertyValuation';
import { getStepInputs, isPropertyDetailDataPrefilled } from '@/tracking/tracking';
import { ToastType } from '@/types';

export function useResultNavigation() {
  const commonStore = useCommonStore();
  const propertyDetailsStore = usePropertyDetailsStore();
  const propertyValuationStore = usePropertyValuationStore();
  const route = useRoute();
  const router = useRouter();
  const { locale } = useI18n();
  const { trackVirtualPageViewWithTreatment } = useTrackingWithTreatment();
  const {
    data: formData,
    step: formStep,
    propertyDetailsData,
    selectedPropertyDetailIndex,
    isIframeActive,
  } = storeToRefs(propertyValuationStore);

  const isUserLoggedIn = computed(() => commonStore.loggedIn);

  const setToastType = (type: ToastType): void => {
    propertyDetailsStore.setToastType(type);
  };

  const getStepInputsForTracking = (): string => {
    return getStepInputs(
      {
        ...formData.value,
        aptDetails: isPropertyDetailDataPrefilled(
          propertyDetailsData.value,
          selectedPropertyDetailIndex.value,
        ),
      },
      route.query.source?.toString() || '',
      formStep.value,
    );
  };

  const getResultPagePath = (path: string, leadId: string): string => {
    const resultPath = router.resolve({
      path,
      query: { leadId, skipRedirect: 'true' },
    }).href;
    return `${globalThis.location.origin}${resultPath}`;
  };

  const handleRoute = async (leadId: string): Promise<void> => {
    if (isUserLoggedIn.value) {
      const response = await getAxiosClient().put(getHomeOwnerWorldApi(`properties/${leadId}`), {});
      if (response.status === 200) {
        setToastType(ToastType.AddSuccess);
        const resolved = router.resolve({
          path: getPropertyDetailsPagePath(locale.value as any, response.data.propertyId),
          query: { source: 'valuation_funnel' },
        });
        window.location.href = resolved.href;
        return;
      }
    }

    const path = RESULT_PATH[locale.value];

    // Always navigate to result page regardless of offer type in iframe
    if (isIframeActive.value && 'parentIFrame' in globalThis) {
      globalThis.parentIFrame.sendMessage({
        type: 'navigate_to_result_page',
        location: getResultPagePath(path, leadId),
      });
      return;
    }

    // Navigate to result page based on offer type
    if (
      formData.value.offerType === OfferType.Selling ||
      formData.value.offerType === OfferType.Other
    ) {
      // Track email result page view
      trackVirtualPageViewWithTreatment({
        subType: 'input_emailResult',
        locale: locale.value,
        stepInputs: getStepInputsForTracking(),
      });

      await router.push({
        path,
        query: { leadId, skipRedirect: 'true' },
      });
    } else {
      // For buying flow, redirect to UBS landing page
      const params = getStepInputsForTracking();
      globalThis.location.href = `${UBS_LANDING_PAGE_PATH[locale.value]}?${params}`;
    }
  };

  return {
    handleRoute,
    getStepInputsForTracking,
    setToastType,
  };
}
